// Self-service: Frentes & Canais (Fase 3) — super_admin.
// Lista frentes (com nº de estoques/canais) e canais; cria frente; wizard de canal
// (frente + adapter deployado + config + secrets cifrados + alocacao % + field_schema).
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Layers, Radio, Pencil } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import {
  coverageLabel,
  isPartialCoverage,
  partialCoverageFromError,
  partialWarning,
  type PartialCoverage,
} from "../../lib/price-coverage";
import { PageHeader } from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { EmptyState } from "../../components/ui/empty-state";
import { Input } from "../../components/ui/input";
import { PasswordField } from "../../components/ui/PasswordField";
import { MlChannelCard } from "../../components/channel/MlChannelCard";
import { parseMlReturn } from "../../lib/ml-channel";
import {
  adapterSwitch,
  buildChannelWizardBody,
  formatCnpj,
  mlChannelNaming,
  mlWizardReady,
  onlyDigits,
  pickMlDefaults,
  type FieldDraft,
  type WizardSnapshot,
} from "../../lib/channel-wizard";
import { Label } from "../../components/ui/label";
import { Select } from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../components/ui/dialog";

interface Front {
  id: number;
  slug: string;
  name: string;
  active: boolean;
  order_n: number;
  stock_sources: number;
  channels: number;
}
interface AdapterType {
  key: string;
  label: string;
  deployed: boolean;
  active: boolean;
}
interface ChannelStockSource {
  id: number;
  sisplan_deposito: string;
  name: string;
  allocation_pct: number;
}
interface AdminChannel {
  id: number;
  slug: string;
  name: string;
  kind: string;
  adapter: string;
  active: boolean;
  // Origem dos dados (plano 2026-10-13 tabela-de-preco-por-canal, 6.3)
  sales_front_slug: string | null;
  sales_front_name: string | null;
  price_type: string | null;
  price_type_label: string | null;
  live_publications: number;
  stock_inherits: boolean;
  stock_sources: ChannelStockSource[];
}
interface PriceTypeItem {
  price_type: string;
  label: string;
  variations: number;
  available: boolean;
  channels: string[];
  /** So com ?channel=<id>: quantas variações ao vivo do canal a tabela cobre. */
  channel_coverage?: { covers: number; total: number };
}
interface StockLocation {
  id: number;
  sisplan_deposito: string;
  name: string;
  slug: string;
  total_unidades: number;
}

export function AdminChannelsPage(): JSX.Element {
  const [frontOpen, setFrontOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState<AdminChannel | null>(null);
  const qc = useQueryClient();

  // Volta do Mercado Livre (/admin/canais?ml=conectado | ?ml=erro&motivo=...): avisa e limpa a URL.
  useEffect(() => {
    const back = parseMlReturn(window.location.search);
    if (!back) return;
    if (back.kind === "ok") toast.success(back.text);
    else toast.error(back.text);
    window.history.replaceState(null, "", window.location.pathname);
    void qc.invalidateQueries({ queryKey: ["ml-status"] });
  }, [qc]);

  const fronts = useQuery<Front[]>({
    queryKey: ["admin-fronts"],
    queryFn: async () => (await api.get<Front[]>("/api/admin/fronts")).data,
  });
  const channels = useQuery<AdminChannel[]>({
    queryKey: ["admin-channels"],
    queryFn: async () => (await api.get<AdminChannel[]>("/api/admin/channels")).data,
  });

  return (
    <div>
      <PageHeader
        title="Frentes & Canais"
        description="Self-service: cadastre frentes de venda, mapeie estoques e crie canais (tipos com adapter deployado)."
      />

      <div className="cards cards--wide" style={{ alignItems: "start" }}>
        {/* Frentes */}
        <div className="card">
          <div className="spread" style={{ marginBottom: ".75rem" }}>
            <h3 style={{ margin: 0, fontSize: ".9375rem", fontWeight: 900 }} className="cluster">
              <Layers className="icon" aria-hidden /> Frentes de venda
            </h3>
            <Button size="sm" variant="secondary" onClick={() => setFrontOpen(true)}>
              <Plus className="icon" aria-hidden /> Nova frente
            </Button>
          </div>
          {fronts.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : fronts.data && fronts.data.length > 0 ? (
            <div className="stack">
              {fronts.data.map((f) => (
                <div
                  key={f.id}
                  className="cluster"
                  style={{
                    minHeight: "3rem",
                    padding: ".5rem .75rem",
                    border: ".0625rem solid var(--color-line)",
                    borderRadius: "var(--radius-md)",
                    justifyContent: "space-between",
                  }}
                >
                  <div className="truncate">
                    <span style={{ fontWeight: 900 }}>{f.name}</span>{" "}
                    <span className="mono" style={{ color: "var(--color-ink-2)", fontSize: ".6875rem" }}>/{f.slug}</span>
                  </div>
                  <div className="cluster" style={{ fontSize: ".6875rem", color: "var(--color-ink-2)", flexShrink: 0 }}>
                    <span>{f.stock_sources} estoques</span>
                    <span>{f.channels} canais</span>
                    {f.active ? <Badge variant="success">ativa</Badge> : <Badge variant="outline">inativa</Badge>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="Sem frentes" description="Crie a primeira frente de venda." />
          )}
        </div>

        {/* Canais */}
        <div className="card">
          <div className="spread" style={{ marginBottom: ".75rem" }}>
            <h3 style={{ margin: 0, fontSize: ".9375rem", fontWeight: 900 }} className="cluster">
              <Radio className="icon" aria-hidden /> Canais
            </h3>
            <Button size="sm" onClick={() => setWizardOpen(true)}>
              <Plus className="icon" aria-hidden /> Novo canal
            </Button>
          </div>
          {channels.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : channels.data && channels.data.length > 0 ? (
            <div className="stack">
              {channels.data.map((ch) => (
                <div
                  key={ch.id}
                  className="stack"
                  style={{
                    gap: ".375rem",
                    minHeight: "3rem",
                    padding: ".5rem .75rem",
                    border: ".0625rem solid var(--color-line)",
                    borderRadius: "var(--radius-md)",
                  }}
                >
                  <div className="cluster" style={{ justifyContent: "space-between" }}>
                    <div className="truncate">
                      <span style={{ fontWeight: 900 }}>{ch.name}</span>{" "}
                      <span className="mono" style={{ color: "var(--color-ink-2)", fontSize: ".6875rem" }}>/{ch.slug}</span>
                    </div>
                    <div className="cluster" style={{ flexShrink: 0 }}>
                      <Badge variant="outline">{ch.adapter}</Badge>
                      {ch.active ? <Badge variant="success">ativo</Badge> : <Badge variant="outline">inativo</Badge>}
                      <Button size="sm" variant="secondary" onClick={() => setEditing(ch)} aria-label={`Editar origem dos dados de ${ch.name}`}>
                        <Pencil className="icon" aria-hidden /> Editar
                      </Button>
                    </div>
                  </div>
                  {/* Origem dos dados: frente, tabela de preco e estoques (6.5) */}
                  <div className="cluster" style={{ fontSize: ".6875rem", color: "var(--color-ink-2)", rowGap: ".125rem" }}>
                    <span>Frente: {ch.sales_front_name ?? "(sem frente)"}</span>
                    <span aria-hidden>·</span>
                    {ch.price_type ? (
                      <span>Tabela: {ch.price_type_label ?? ch.price_type}</span>
                    ) : (
                      <Badge variant="warning">sem tabela de preço</Badge>
                    )}
                    <span aria-hidden>·</span>
                    <span>
                      Estoque: {describeStock(ch)}
                    </span>
                  </div>
                  {ch.adapter === "ml" ? <MlChannelCard slug={ch.slug} name={ch.name} /> : null}
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="Sem canais" description="Crie um canal pelo wizard." />
          )}
        </div>
      </div>

      {frontOpen ? <CreateFrontDialog open={frontOpen} onOpenChange={setFrontOpen} /> : null}
      {wizardOpen ? (
        <ChannelWizardDialog
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          fronts={fronts.data ?? []}
          channels={channels.data ?? []}
        />
      ) : null}
      {editing ? (
        <ChannelSourcesDialog
          channel={editing}
          fronts={fronts.data ?? []}
          open={editing != null}
          onOpenChange={(o) => {
            if (!o) setEditing(null);
          }}
        />
      ) : null}
    </div>
  );
}

function describeStock(ch: AdminChannel): string {
  const list = ch.stock_sources ?? [];
  if (list.length === 0) {
    if (!ch.stock_inherits) return "nenhum estoque";
    return ch.sales_front_slug ? "herda da frente (nenhum estoque)" : "sem frente (nenhum estoque)";
  }
  const parts = list.map((s) =>
    ch.stock_inherits || s.allocation_pct === 100
      ? `${s.sisplan_deposito} ${s.name}`
      : `${s.sisplan_deposito} ${s.name} ${s.allocation_pct}%`,
  );
  return `${ch.stock_inherits ? "herda da frente" : "próprio"} (${parts.join(", ")})`;
}

/* ============================================================
 * Tabela de preco do Sisplan — select compartilhado pelo wizard e pelo Editar.
 * Lista vem de GET /api/admin/price-types (o que esta espelhado em sisplan.prices).
 * ============================================================ */
// Com channelId (Editar) a API devolve tambem quanto cada tabela cobre das
// variacoes ao vivo DAQUELE canal; sem ele (wizard, canal ainda sem produto
// ao vivo) so o total da tabela no Sisplan.
function usePriceTypes(channelId?: number) {
  return useQuery<{ price_types: PriceTypeItem[] }>({
    queryKey: ["admin-price-types", channelId ?? null],
    queryFn: async () =>
      (
        await api.get<{ price_types: PriceTypeItem[] }>("/api/admin/price-types", {
          params: channelId !== undefined ? { channel: channelId } : undefined,
        })
      ).data,
  });
}

function PriceTypeSelect({
  id,
  value,
  onChange,
  disabled,
  channelId,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  channelId?: number;
}): JSX.Element {
  const priceTypes = usePriceTypes(channelId);
  const items = priceTypes.data?.price_types ?? [];
  return (
    <div className="field">
      <Label htmlFor={id}>Tabela de preço (Sisplan)</Label>
      <Select id={id} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled || priceTypes.isLoading}>
        <option value="">(sem tabela: a loja fica com o último preço publicado)</option>
        {items.map((p) => (
          <option key={p.price_type} value={p.price_type} disabled={!p.available}>
            {p.label}
            {!p.available
              ? " · sem preços no Sisplan"
              : p.channel_coverage && p.channel_coverage.total > 0
                ? ` · ${coverageLabel(p.channel_coverage.covers, p.channel_coverage.total)}`
                : ` · ${p.variations.toLocaleString("pt-BR")} variações`}
          </option>
        ))}
      </Select>
      {priceTypes.isError ? (
        <p className="field__hint" style={{ color: "var(--color-warn)" }}>
          Não deu para ler as tabelas do Sisplan agora ({apiErrorMessage(priceTypes.error)}).
        </p>
      ) : (
        <p className="field__hint">
          O preço publicado na loja é o desta tabela. Tabela nova do Sisplan aparece aqui quando a integração a mapear.
        </p>
      )}
    </div>
  );
}

/* ============================================================
 * Estoques (alocacao %) — compartilhado pelo wizard e pelo Editar.
 * ============================================================ */
function StockAllocationFields({
  allocations,
  onChange,
  disabled,
}: {
  allocations: Record<number, number>;
  onChange: (next: Record<number, number>) => void;
  disabled?: boolean;
}): JSX.Element {
  const stock = useQuery<{ locations: StockLocation[] }>({
    queryKey: ["admin-stock-locations"],
    queryFn: async () => (await api.get<{ locations: StockLocation[] }>("/api/admin/stock/locations")).data,
  });
  return (
    <div className="field">
      <Label>Estoques (alocação %), vazio = herda da frente</Label>
      <div className="stack" style={{ gap: "var(--sp-1)", border: "var(--border)", borderRadius: "var(--radius-md)", padding: "var(--sp-2)" }}>
        {(stock.data?.locations ?? []).map((loc) => (
          <div key={loc.id} className="spread text-md text-ink">
            <span>
              <span className="mono" style={{ color: "var(--color-ink-2)" }}>{loc.sisplan_deposito}</span> {loc.name}{" "}
              <span className="text-ink-2">({loc.total_unidades.toLocaleString("pt-BR")} un)</span>
            </span>
            <Input
              type="number"
              min={0}
              max={100}
              style={{ width: "5rem" }}
              placeholder="%"
              aria-label={`Alocação do estoque ${loc.name} em porcentagem`}
              disabled={disabled}
              value={allocations[loc.id] != null ? String(allocations[loc.id]) : ""}
              onChange={(e) => {
                const next = { ...allocations };
                if (e.target.value === "") delete next[loc.id];
                else next[loc.id] = Math.max(0, Math.min(100, Number(e.target.value)));
                onChange(next);
              }}
            />
          </div>
        ))}
      </div>
      <p className="field__hint">
        Deixe tudo vazio para herdar os estoques da frente. A loja recebe o estoque recalculado em até 5 minutos.
      </p>
    </div>
  );
}

// Caixa vazia = fora da lista; caixa com 0 = override de 0% (QA P2-01: zerar
// tudo e um override zerado, nao "herdar da frente").
function allocationsToList(allocations: Record<number, number>): Array<{ stock_source_id: number; allocation_pct: number }> {
  return Object.entries(allocations)
    .filter(([, pct]) => Number.isFinite(pct))
    .map(([idStr, pct]) => ({ stock_source_id: Number(idStr), allocation_pct: Number(pct) }));
}

/* ============================================================
 * Editar origem dos dados do canal: frente, tabela de preco e estoques.
 * PATCH /api/channels/:slug envia so o que mudou (plano 6.2 / 6.5).
 * ============================================================ */
function ChannelSourcesDialog({
  channel,
  fronts,
  open,
  onOpenChange,
}: {
  channel: AdminChannel;
  fronts: Front[];
  open: boolean;
  onOpenChange: (o: boolean) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [frontSlug, setFrontSlug] = useState(channel.sales_front_slug ?? "");
  const [priceType, setPriceType] = useState(channel.price_type ?? "");
  const [allocations, setAllocations] = useState<Record<number, number>>({});
  const [allocationsLoaded, setAllocationsLoaded] = useState(false);
  // 422 price_type_partial da API: a tabela escolhida nao cobre todas as variacoes
  // ao vivo. Fica guardado ate a pessoa confirmar ("Trocar assim mesmo") ou mudar a tabela.
  const [partial, setPartial] = useState<PartialCoverage | null>(null);
  const priceTypes = usePriceTypes(channel.id);

  const current = useQuery<{ inherits_from_front: boolean; allocations: Array<{ id: number; allocation_pct: number | string }> }>({
    queryKey: ["admin-channel-stock-sources", channel.slug],
    queryFn: async () =>
      (await api.get<{ inherits_from_front: boolean; allocations: Array<{ id: number; allocation_pct: number | string }> }>(
        `/api/admin/channels/${channel.slug}/stock-sources`,
      )).data,
  });
  useEffect(() => {
    if (current.data && !allocationsLoaded) {
      const map: Record<number, number> = {};
      for (const a of current.data.allocations) map[a.id] = Number(a.allocation_pct);
      setAllocations(map);
      setAllocationsLoaded(true);
    }
  }, [current.data, allocationsLoaded]);

  const initialAllocations = (() => {
    const map: Record<number, number> = {};
    for (const a of current.data?.allocations ?? []) map[a.id] = Number(a.allocation_pct);
    return map;
  })();
  const frontChanged = frontSlug !== (channel.sales_front_slug ?? "");
  const priceChanged = priceType !== (channel.price_type ?? "");
  const allocationsChanged =
    allocationsLoaded && JSON.stringify(allocationsToList(allocations)) !== JSON.stringify(allocationsToList(initialAllocations));
  const changed = frontChanged || priceChanged || allocationsChanged;

  // Estoque efetivo vazio: sem override e frente sem estoques, ou override com tudo 0.
  const selectedFront = fronts.find((f) => f.slug === frontSlug);
  const overrideList = allocationsToList(allocations);
  const stockWillBeEmpty =
    (allocationsChanged || frontChanged) &&
    (overrideList.length === 0
      ? !selectedFront || selectedFront.stock_sources === 0
      : overrideList.every((a) => a.allocation_pct === 0));

  const selectedPriceItem = priceTypes.data?.price_types.find((p) => p.price_type === priceType);
  const priceTableLabel = selectedPriceItem?.label ?? priceType;
  // Aviso antes mesmo de salvar, com o que a API ja mediu para este canal.
  const preCoverage = selectedPriceItem?.channel_coverage;
  const prePartial =
    priceChanged && priceType && preCoverage && isPartialCoverage(preCoverage.covers, preCoverage.total)
      ? { sem_preco: preCoverage.total - preCoverage.covers, total: preCoverage.total }
      : null;

  const save = useMutation({
    mutationFn: async (force: boolean) => {
      const body: Record<string, unknown> = {};
      if (frontChanged) body.sales_front_slug = frontSlug || null;
      if (priceChanged) body.price_type = priceType || null;
      if (priceChanged && force) body.force = true;
      if (allocationsChanged) body.allocations = overrideList;
      return (await api.patch<{ ok: boolean; republish_enqueued?: number }>(`/api/channels/${channel.slug}`, body)).data;
    },
    onSuccess: (d) => {
      setPartial(null);
      const n = d.republish_enqueued ?? 0;
      toast.success(n > 0 ? `Canal salvo · ${n} produto${n === 1 ? "" : "s"} na fila de republicação` : "Canal salvo");
      void qc.invalidateQueries({ queryKey: ["admin-channels"] });
      void qc.invalidateQueries({ queryKey: ["admin-fronts"] });
      void qc.invalidateQueries({ queryKey: ["admin-price-types"] });
      void qc.invalidateQueries({ queryKey: ["admin-channel-stock-sources", channel.slug] });
      onOpenChange(false);
    },
    onError: (e) => {
      const p = partialCoverageFromError(e);
      if (p) setPartial(p);
      else toast.error(apiErrorMessage(e));
    },
  });

  const live = channel.live_publications;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Origem dos dados — {channel.name}</DialogTitle>
          <DialogDescription>
            De qual frente, tabela de preço e estoques do Sisplan este canal puxa. {live > 0 ? `${live} produto${live === 1 ? "" : "s"} ao vivo.` : "Nenhum produto ao vivo."}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (changed) save.mutate(false);
          }}
          className="stack"
          style={{ gap: "var(--sp-3)" }}
        >
          <div className="field">
            <Label htmlFor="cs-front">Frente</Label>
            <Select id="cs-front" value={frontSlug} onChange={(e) => setFrontSlug(e.target.value)}>
              <option value="">(sem frente)</option>
              {fronts.map((f) => (
                <option key={f.slug} value={f.slug}>{f.name}</option>
              ))}
            </Select>
            <p className="field__hint">A frente só agrupa estoques herdados; trocar a frente não muda a tabela de preço.</p>
          </div>

          <PriceTypeSelect
            id="cs-price-type"
            value={priceType}
            channelId={channel.id}
            onChange={(next) => {
              setPartial(null);
              setPriceType(next);
            }}
          />
          {priceChanged && live > 0 && channel.active ? (
            <p className="field__hint" style={{ color: "var(--color-warn)", fontWeight: 700 }}>
              {priceType
                ? `${live === 1 ? "O 1 produto ao vivo deste canal será republicado" : `Os ${live} produtos ao vivo deste canal serão republicados`} com os preços da tabela "${priceTableLabel}".`
                : `${live === 1 ? "O 1 produto ao vivo deste canal será republicado" : `Os ${live} produtos ao vivo deste canal serão republicados`} sem preço novo: a loja mantém o último preço publicado de cada um.`}
            </p>
          ) : null}
          {prePartial && !partial ? (
            <p className="field__hint" style={{ color: "var(--color-warn)", fontWeight: 700 }}>
              {partialWarning(priceTableLabel, prePartial)} Ao salvar, vamos pedir sua confirmação.
            </p>
          ) : null}
          {partial ? (
            <div
              role="alert"
              className="stack"
              style={{ gap: "var(--sp-2)", border: "var(--border)", borderRadius: "var(--radius-md)", padding: "var(--sp-2)" }}
            >
              <p className="text-md text-ink" style={{ fontWeight: 700 }}>
                {partialWarning(priceTableLabel, partial)}
              </p>
              <p className="field__hint">
                A loja não avisa quem compra: essas variações simplesmente não recebem o preço novo.
                {partial.amostra.length > 0
                  ? ` Exemplos de variações sem preço nesta tabela: ${partial.amostra.join(", ")}.`
                  : ""}
              </p>
              <div className="spread">
                <Button type="button" variant="outline" onClick={() => setPartial(null)} disabled={save.isPending}>
                  Escolher outra tabela
                </Button>
                <Button type="button" onClick={() => save.mutate(true)} disabled={save.isPending}>
                  Trocar assim mesmo
                </Button>
              </div>
            </div>
          ) : null}

          {current.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : current.isError ? (
            // Sem o estoque atual nao da para editar com seguranca (o que fosse
            // digitado nao iria no PATCH): mostra o erro e deixa frente/tabela editaveis.
            <p className="field__hint" style={{ color: "var(--color-warn)" }}>
              Não deu para ler os estoques atuais deste canal ({apiErrorMessage(current.error)}). Feche e abra de novo para editar o estoque.
            </p>
          ) : (
            <StockAllocationFields allocations={allocations} onChange={setAllocations} />
          )}
          {stockWillBeEmpty ? (
            <p className="field__hint" style={{ color: "var(--color-warn)", fontWeight: 700 }}>
              Este canal ficará sem estoque: a loja zera em até 5 minutos.
            </p>
          ) : null}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">Cancelar</Button>
            </DialogClose>
            <Button type="submit" disabled={!changed || save.isPending || partial !== null}>
              {stockWillBeEmpty ? "Salvar e zerar estoque" : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateFrontDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }): JSX.Element {
  const qc = useQueryClient();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const mut = useMutation({
    mutationFn: async () => (await api.post("/api/admin/fronts", { slug, name })).data,
    onSuccess: () => {
      toast.success("Frente criada");
      void qc.invalidateQueries({ queryKey: ["admin-fronts"] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nova frente de venda</DialogTitle>
          <DialogDescription>Agrupa estoques e canais (ex.: atacado, varejo, outlet).</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mut.mutate();
          }}
          className="stack"
          style={{ gap: "var(--sp-3)" }}
        >
          <div className="field">
            <Label htmlFor="nf-name">Nome</Label>
            <Input id="nf-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="field">
            <Label htmlFor="nf-slug">Slug</Label>
            <Input
              id="nf-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
              placeholder="ex: marketplace-ml"
              required
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">Cancelar</Button>
            </DialogClose>
            <Button type="submit" disabled={mut.isPending}>Criar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ChannelWizardDialog({
  open,
  onOpenChange,
  fronts,
  channels,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  fronts: Front[];
  channels: AdminChannel[];
}): JSX.Element {
  const qc = useQueryClient();
  // Sugere a tabela de um canal irmao da mesma frente (dado, nao mapa; plano D8).
  const siblingPriceType = useCallback(
    (front: string): string => channels.find((c) => c.sales_front_slug === front && c.price_type)?.price_type ?? "",
    [channels],
  );
  const [frontSlug, setFrontSlugState] = useState(fronts[0]?.slug ?? "");
  const [priceType, setPriceType] = useState(siblingPriceType(fronts[0]?.slug ?? ""));
  const setFrontSlug = (next: string): void => {
    setFrontSlugState(next);
    // Sugestao segue a frente: canal irmao com tabela -> essa; sem irmao -> vazio.
    setPriceType(siblingPriceType(next));
  };
  const [adapter, setAdapter] = useState("wc");
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState("marketplace");
  const [wcUrl, setWcUrl] = useState("");
  const [ck, setCk] = useState("");
  const [cs, setCs] = useState("");
  const [mlClientId, setMlClientId] = useState("");
  const [mlClientSecret, setMlClientSecret] = useState("");
  const [mlCnpj, setMlCnpj] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [allocations, setAllocations] = useState<Record<number, number>>({});
  const [fields, setFields] = useState<FieldDraft[]>([]);

  // Tipo ML: um canal por conta (CNPJ). Nome "Mercado Livre <CNPJ>" e slug "ml-<digitos>" nascem do CNPJ e
  // acompanham ele ate a pessoa editar um dos dois na mao. Client ID e CNPJ padrao vem de settings (API);
  // frente e tabela de varejo vem dos dados da API (nunca de codigo). O caminho do WooCommerce nao muda.
  const mlDefaults = useQuery<{ client_id: string; cnpj: string }>({
    queryKey: ["ml-defaults"],
    enabled: adapter === "ml",
    queryFn: async () => (await api.get<{ client_id: string; cnpj: string }>("/api/admin/ml/defaults")).data,
  });
  const priceTypesQ = usePriceTypes();
  const mlAppliedRef = useRef(false);
  const applyMlNaming = (cnpj: string): void => {
    const n = mlChannelNaming(cnpj);
    if (!nameTouched) setName(n.name);
    if (!slugTouched) setSlug(n.slug);
  };
  const preMlRef = useRef<WizardSnapshot | null>(null);
  const changeAdapter = (next: string): void => {
    const sw = adapterSwitch(adapter, next, { frontSlug, priceType, name, slug, nameTouched, slugTouched }, preMlRef.current);
    preMlRef.current = sw.restore ? null : sw.save;
    setAdapter(next);
    if (sw.restore) {
      // Voltou do ML: frente, tabela e nome ficam como estavam no tipo anterior.
      setFrontSlugState(sw.restore.frontSlug);
      setPriceType(sw.restore.priceType);
      setName(sw.restore.name);
      setSlug(sw.restore.slug);
      setNameTouched(sw.restore.nameTouched);
      setSlugTouched(sw.restore.slugTouched);
    } else if (next === "ml") applyMlNaming(mlCnpj);
  };
  const changeMlCnpj = (next: string): void => {
    setMlCnpj(next);
    applyMlNaming(next);
  };
  useEffect(() => {
    if (adapter !== "ml") {
      mlAppliedRef.current = false;
      return;
    }
    if (mlAppliedRef.current || mlDefaults.isLoading || priceTypesQ.isLoading) return;
    mlAppliedRef.current = true;
    const d = mlDefaults.data;
    if (d?.client_id) setMlClientId((cur) => cur || d.client_id);
    if (d?.cnpj) {
      setMlCnpj((cur) => cur || d.cnpj);
      const n = mlChannelNaming(d.cnpj);
      if (!nameTouched) setName(n.name);
      if (!slugTouched) setSlug(n.slug);
    }
    const pick = pickMlDefaults(fronts, priceTypesQ.data?.price_types ?? [], siblingPriceType);
    if (pick.frontSlug) setFrontSlugState(pick.frontSlug);
    if (pick.priceType) setPriceType(pick.priceType);
  }, [adapter, mlDefaults.isLoading, mlDefaults.data, priceTypesQ.isLoading, priceTypesQ.data, fronts, siblingPriceType, nameTouched, slugTouched]);

  const adapterTypes = useQuery<AdapterType[]>({
    queryKey: ["admin-adapter-types"],
    queryFn: async () => (await api.get<AdapterType[]>("/api/admin/adapter-types")).data,
  });
  const create = useMutation({
    mutationFn: async () => {
      const body = buildChannelWizardBody({
        frontSlug,
        slug,
        name,
        kind,
        adapter,
        priceType,
        allocations: allocationsToList(allocations),
        fields,
        wcUrl,
        consumerKey: ck,
        consumerSecret: cs,
        mlClientId,
        mlClientSecret,
        mlCnpj,
      });
      return (await api.post("/api/admin/channels/wizard", body)).data as { secrets_mode: string };
    },
    onSuccess: (d) => {
      toast.success(
        adapter === "ml"
          ? "Canal criado. Agora clique em Conectar ao Mercado Livre no cartão do canal."
          : `Canal criado (credenciais: ${d.secrets_mode === "encrypted" ? "cifradas" : "em config"})`,
      );
      void qc.invalidateQueries({ queryKey: ["admin-channels"] });
      void qc.invalidateQueries({ queryKey: ["admin-fronts"] });
      void qc.invalidateQueries({ queryKey: ["admin-price-types"] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const deployed = (adapterTypes.data ?? []).filter((a) => a.deployed);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Novo canal</DialogTitle>
          <DialogDescription>
            Só tipos com adapter implantado. Tipo novo (marketplace/WhatsApp/Instagram) exige adapter no worker.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
          className="stack"
          style={{ gap: "var(--sp-3)" }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="field">
              <Label htmlFor="cw-front">Frente</Label>
              <Select id="cw-front" value={frontSlug} onChange={(e) => setFrontSlug(e.target.value)}>
                <option value="">(sem frente)</option>
                {fronts.map((f) => (
                  <option key={f.slug} value={f.slug}>{f.name}</option>
                ))}
              </Select>
            </div>
            <div className="field">
              <Label htmlFor="cw-adapter">Tipo (adapter)</Label>
              <Select id="cw-adapter" value={adapter} onChange={(e) => changeAdapter(e.target.value)}>
                {deployed.map((a) => (
                  <option key={a.key} value={a.key}>{a.label}</option>
                ))}
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="field">
              <Label htmlFor="cw-name">Nome</Label>
              <Input id="cw-name" value={name} onChange={(e) => { setNameTouched(true); setName(e.target.value); }} required />
            </div>
            <div className="field">
              <Label htmlFor="cw-slug">Slug</Label>
              <Input
                id="cw-slug"
                value={slug}
                onChange={(e) => { setSlugTouched(true); setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-")); }}
                required
              />
            </div>
          </div>
          <div className="field">
            <Label htmlFor="cw-kind">Categoria (kind)</Label>
            <Input id="cw-kind" value={kind} onChange={(e) => setKind(e.target.value)} placeholder="marketplace" />
          </div>

          <PriceTypeSelect id="cw-price-type" value={priceType} onChange={setPriceType} />

          {adapter === "wc" ? (
            <div className="stack" style={{ gap: "var(--sp-2)", border: "var(--border)", borderRadius: "var(--radius-md)", padding: "var(--sp-2)" }}>
              <div className="filters__group-label">Credenciais WooCommerce (cifradas at-rest)</div>
              <div className="field">
                <Label htmlFor="cw-url">URL da loja</Label>
                <Input id="cw-url" value={wcUrl} onChange={(e) => setWcUrl(e.target.value)} placeholder="https://loja.exemplo.com" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="field">
                  <Label htmlFor="cw-ck">Consumer Key</Label>
                  <Input id="cw-ck" value={ck} onChange={(e) => setCk(e.target.value)} autoComplete="off" />
                </div>
                <div className="field">
                  <Label htmlFor="cw-cs">Consumer Secret</Label>
                  <PasswordField id="cw-cs" value={cs} onChange={(e) => setCs(e.target.value)} autoComplete="new-password" />
                </div>
              </div>
            </div>
          ) : null}

          {adapter === "ml" ? (
            <div className="stack" style={{ gap: "var(--sp-2)", border: "var(--border)", borderRadius: "var(--radius-md)", padding: "var(--sp-2)" }}>
              <div className="filters__group-label">Credenciais do app no Mercado Livre (cifradas at-rest)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="field">
                  <Label htmlFor="cw-mlcnpj">CNPJ da conta</Label>
                  <Input id="cw-mlcnpj" value={mlCnpj} onChange={(e) => changeMlCnpj(e.target.value)} autoComplete="off" inputMode="numeric" placeholder="00.000.000/0000-00" />
                  <p className="field__hint">Um canal por conta. O nome e o slug nascem daqui{onlyDigits(mlCnpj).length === 14 ? `: ${formatCnpj(mlCnpj)}` : ""}.</p>
                </div>
                <div className="field">
                  <Label htmlFor="cw-mlid">Client ID</Label>
                  <Input id="cw-mlid" value={mlClientId} onChange={(e) => setMlClientId(e.target.value)} autoComplete="off" inputMode="numeric" required />
                </div>
                <div className="field">
                  <Label htmlFor="cw-mlsecret">Client Secret</Label>
                  <PasswordField id="cw-mlsecret" value={mlClientSecret} onChange={(e) => setMlClientSecret(e.target.value)} autoComplete="new-password" spellCheck={false} required />
                </div>
              </div>
              <p className="field__hint">
                O Client ID e o Client Secret estão no painel de desenvolvedor do Mercado Livre, no app "Vitrine Hiit". Confira a frente (varejo) e a tabela de preço do varejo consumidor. Depois de criar o canal, clique em
                Conectar ao Mercado Livre no cartão dele e entre como administrador da conta deste CNPJ.
              </p>
            </div>
          ) : null}

          {/* Alocação de estoque */}
          <StockAllocationFields allocations={allocations} onChange={setAllocations} />

          {/* field_schema simples (o canal ML ja nasce com os campos dele) */}
          {adapter !== "ml" ? (
          <div className="field">
            <div className="spread">
              <Label>Campos do canal (field_schema)</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setFields((f) => [...f, { key: "", label: "", required: false }])}
              >
                <Plus className="icon" style={{ width: ".75rem", height: ".75rem" }} /> campo
              </Button>
            </div>
            {fields.map((f, i) => (
              <div key={i} className="cluster">
                <Input
                  placeholder="key (ex: ean)"
                  value={f.key}
                  onChange={(e) => setFields((cur) => cur.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))}
                  className="flex-1"
                />
                <Input
                  placeholder="label"
                  value={f.label}
                  onChange={(e) => setFields((cur) => cur.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                  className="flex-1"
                />
                <label className="cluster text-micro text-ink-2" style={{ gap: ".25rem" }}>
                  <input
                    type="checkbox"
                    checked={f.required}
                    onChange={(e) => setFields((cur) => cur.map((x, j) => (j === i ? { ...x, required: e.target.checked } : x)))}
                  />
                  obrig.
                </label>
              </div>
            ))}
          </div>
          ) : null}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">Cancelar</Button>
            </DialogClose>
            <Button type="submit" disabled={create.isPending || (adapter === "ml" && !mlWizardReady(mlClientId, mlClientSecret))}>Criar canal</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
